/**
 * TX Ingest API
 *
 * Handles TX file ingest workflow:
 * QC → Auto-fix (loudness, mono→stereo) → MXF encode → Upload to playout
 */

import axios from '@/axios'

/**
 * Get TX Ingest workflow template
 */
export async function getWorkflowTemplate() {
  const response = await axios.get(`/api/tx-ingest/workflow`)
  return response.data
}

/**
 * Submit a TX Ingest job
 *
 * @param {Object} params
 * @param {number} params.episodeId - Episode ID (for episodic content)
 * @param {number} params.mediaId - Media ID (for non-episodic content)
 * @param {number} params.storageConfigId - Source storage configuration
 * @param {string} params.sourcePath - Path to TX file in storage
 * @param {boolean} params.enableNormalization - Apply audio normalization (default: true)
 * @param {string} params.loudnessStandard - ebu_r128, atsc_a85, op59 (default: ebu_r128)
 * @param {boolean} params.enableMxfEncode - Encode to MXF for playout (default: true)
 */
export async function submitJob(params) {
  const response = await axios.post(`/api/tx-ingest/submit`, params)
  return response.data
}

/**
 * Get TX Ingest job status
 *
 * @param {string} jobId - Job UUID or ID
 */
export async function getJobStatus(jobId) {
  const response = await axios.get(`/api/tx-ingest/job/${jobId}`)
  return response.data
}

/**
 * Poll job status until complete
 *
 * @param {string} jobId - Job UUID or ID
 * @param {function} onProgress - Progress callback (status, progress, data)
 * @param {number} intervalMs - Poll interval in ms (default: 3000)
 * @param {number} maxAttempts - Max poll attempts (default: 200 = ~10 min)
 */
export async function pollJobStatus(jobId, onProgress, intervalMs = 3000, maxAttempts = 200) {
  let attempts = 0

  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        attempts++
        const result = await getJobStatus(jobId)

        if (onProgress) {
          onProgress(result.job.status, result.job.progress, result)
        }

        // Check if job is complete
        if (['completed', 'failed', 'cancelled'].includes(result.job.status)) {
          resolve(result)
          return
        }

        // Check max attempts
        if (attempts >= maxAttempts) {
          reject(new Error('Job polling timeout'))
          return
        }

        // Continue polling
        setTimeout(poll, intervalMs)
      } catch (error) {
        reject(error)
      }
    }

    poll()
  })
}

// VOD Destinations

/**
 * Get all VOD delivery destinations
 */
export async function getVodDestinations() {
  const response = await axios.get(`/api/vod-destinations`)
  return response.data
}

/**
 * Create a VOD delivery destination
 */
export async function createVodDestination(destination) {
  const response = await axios.post(`/api/vod-destinations`, destination)
  return response.data
}

/**
 * Update a VOD delivery destination
 */
export async function updateVodDestination(id, updates) {
  const response = await axios.put(`/api/vod-destinations/${id}`, updates)
  return response.data
}

/**
 * Delete a VOD delivery destination
 */
export async function deleteVodDestination(id) {
  const response = await axios.delete(`/api/vod-destinations/${id}`)
  return response.data
}

// VOD Delivery Jobs

/**
 * Get VOD delivery jobs
 */
export async function getVodDeliveryJobs(params = {}) {
  const response = await axios.get(`/api/vod-delivery/jobs`, { params })
  return response.data
}

/**
 * Submit a manual VOD delivery job
 */
export async function submitVodDelivery(params) {
  const response = await axios.post(`/api/vod-delivery/submit`, params)
  return response.data
}

/**
 * Approve a pending VOD delivery job
 */
export async function approveVodDelivery(jobId) {
  const response = await axios.post(`/api/vod-delivery/approve/${jobId}`)
  return response.data
}

// Batch TX Ingest (Season Level)

/**
 * Suggest TX files for all episodes in a season
 *
 * Scans a storage path and matches TX files to episodes based on filename patterns.
 *
 * @param {Object} params
 * @param {number} params.mediaId - Media/season ID
 * @param {number} params.storageConfigId - Storage config to scan
 * @param {string} params.storagePath - Path prefix to scan (e.g., /TX_MASTERS/SHOW_NAME/SEASON_01/)
 * @param {string} params.customPattern - Optional custom pattern hint
 */
export async function suggestTxFilesForSeason(params) {
  const response = await axios.post(`/api/tx-ingest/suggest-season`, params)
  return response.data
}

/**
 * Submit batch TX Ingest jobs for selected episodes
 *
 * Links suggested files to episodes and submits QC jobs.
 * User selects which matched episodes to process from the suggestions.
 *
 * @param {Object} params
 * @param {number} params.mediaId - Media/season ID
 * @param {number} params.storageConfigId - Source storage config
 * @param {number} params.workflowTemplateId - Workflow template (tx_ingest)
 * @param {Array} params.episodes - Array of { episodeId, storagePath } to process
 * @param {boolean} params.skipQc - Skip QC (not recommended)
 */
export async function submitBatchTxIngest(params) {
  const response = await axios.post(`/api/tx-ingest/submit-batch`, params)
  return response.data
}

export default {
  getWorkflowTemplate,
  submitJob,
  getJobStatus,
  pollJobStatus,
  getVodDestinations,
  createVodDestination,
  updateVodDestination,
  deleteVodDestination,
  getVodDeliveryJobs,
  submitVodDelivery,
  approveVodDelivery,
  suggestTxFilesForSeason,
  submitBatchTxIngest
}
